import React from "react";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";

const ScheduleModal = ({
  isOpen,
  onClose,
  scheduleSettings,
  setScheduleSettings,
  saveScheduleSettings,
  isProcessing,
  setIsProcessing,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Upload Schedule Settings</h2>
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h4 className="font-medium text-blue-800 mb-2">How Scheduling Works:</h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-blue-700">
              <li>
                When scheduling is enabled, uploads will only be allowed during the specified time
                windows
              </li>
              <li>
                The system checks every minute to enable/disable uploads based on the schedule
              </li>
              <li>
                Manual overrides (using the toggle) will persist until the next schedule check
              </li>
              <li>Special dates completely disable uploads for that day, regardless of time</li>
              <li>Maintenance mode overrides all other settings</li>
            </ul>
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800 font-medium">
                Note: Time entries use 12-hour format. For example, "4:00 PM" means 16:00.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <label className="text-gray-700 font-medium">Enable Scheduling</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={scheduleSettings.isSchedulingEnabled}
                onChange={(e) =>
                  setScheduleSettings({
                    ...scheduleSettings,
                    isSchedulingEnabled: e.target.checked,
                  })
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {scheduleSettings.isSchedulingEnabled && (
            <div className="space-y-4">
              {Object.entries(scheduleSettings.days).map(([day, settings]) => (
                <div key={day} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-gray-700 font-medium capitalize">{day}</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.enabled}
                        onChange={(e) =>
                          setScheduleSettings({
                            ...scheduleSettings,
                            days: {
                              ...scheduleSettings.days,
                              [day]: {
                                ...settings,
                                enabled: e.target.checked,
                              },
                            },
                          })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {settings.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Start Time</label>
                        <input
                          type="time"
                          value={settings.start}
                          onChange={(e) =>
                            setScheduleSettings({
                              ...scheduleSettings,
                              days: {
                                ...scheduleSettings.days,
                                [day]: {
                                  ...settings,
                                  start: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">End Time</label>
                        <input
                          type="time"
                          value={settings.end}
                          onChange={(e) =>
                            setScheduleSettings({
                              ...scheduleSettings,
                              days: {
                                ...scheduleSettings.days,
                                [day]: {
                                  ...settings,
                                  end: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-2">Special Dates</h3>
                <div className="space-y-2">
                  {scheduleSettings.exceptions.map((exception, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="date"
                        value={exception.date}
                        onChange={(e) => {
                          const newExceptions = [...scheduleSettings.exceptions];
                          newExceptions[index].date = e.target.value;
                          setScheduleSettings({
                            ...scheduleSettings,
                            exceptions: newExceptions,
                          });
                        }}
                        className="p-2 border border-gray-300 rounded-md"
                      />
                      <button
                        onClick={() => {
                          const newExceptions = [...scheduleSettings.exceptions];
                          newExceptions.splice(index, 1);
                          setScheduleSettings({
                            ...scheduleSettings,
                            exceptions: newExceptions,
                          });
                        }}
                        className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      setScheduleSettings({
                        ...scheduleSettings,
                        exceptions: [...scheduleSettings.exceptions, { date: "" }],
                      })
                    }
                    className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Add Special Date
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setIsProcessing(true);
                try {
                  await saveScheduleSettings();
                  onClose();
                } catch (error) {
                  toast.error("Failed to save schedule");
                  console.error("Save error:", error);
                } finally {
                  setIsProcessing(false);
                }
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center justify-center min-w-[80px]"
              disabled={isProcessing}
            >
              {isProcessing ? <ClipLoader color="#ffffff" size={20} /> : "Save Schedule"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
